import { parseMarkdown } from '../utils/markdown';

const createMovieSubmissionsContext = () => {
  const emptyContext = () => {
    throw new Error('Webpack require.context is not available in this environment.');
  };
  emptyContext.keys = () => [];

  try {
    return require.context('../data/movie-submissions', true, /\.md$/);
  } catch {
    return emptyContext;
  }
};

let movieSubmissionsContext = createMovieSubmissionsContext();
const ALLOWED_COLUMNS = new Set(['favoritos', 'assistidos', 'quero-ver']);
const DEFAULT_CATEGORY_LABELS = {
  series: 'Series',
  filmes: 'Filmes',
  animacoes: 'Animacoes',
  geral: 'Geral',
};

const stripDiacritics = (value) => {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const normalizeWhitespace = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.replace(/\s+/g, ' ').trim();
};

const normalizeColumn = (value) => {
  const normalized = normalizeWhitespace(value).toLowerCase();
  return ALLOWED_COLUMNS.has(normalized) ? normalized : 'favoritos';
};

const normalizeCategoryKey = (value) => {
  const normalized = stripDiacritics(normalizeWhitespace(value))
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || 'geral';
};

const resolveCategory = (frontmatterCategoryKey, frontmatterCategory, pathCategory) => {
  const hasExplicitCategoryKey = Boolean(normalizeWhitespace(frontmatterCategoryKey));

  if (hasExplicitCategoryKey) {
    const normalizedCategoryKey = normalizeCategoryKey(frontmatterCategoryKey);
    return {
      categoryKey: normalizedCategoryKey,
      category: formatCategoryLabel(normalizedCategoryKey, frontmatterCategory),
    };
  }

  const hasFrontmatterCategory = Boolean(normalizeWhitespace(frontmatterCategory));
  if (hasFrontmatterCategory) {
    // In the new format, `category` may be a custom label; derive key from it to avoid collisions.
    const normalizedCategoryKey = normalizeCategoryKey(frontmatterCategory);
    return {
      categoryKey: normalizedCategoryKey,
      category: formatCategoryLabel(normalizedCategoryKey, frontmatterCategory),
    };
  }

  const normalizedCategoryKey = normalizeCategoryKey(pathCategory || '');
  return {
    categoryKey: normalizedCategoryKey,
    category: formatCategoryLabel(normalizedCategoryKey, ''),
  };
};

const formatCategoryLabel = (categoryKey, providedLabel) => {
  const cleanedLabel = normalizeWhitespace(providedLabel);
  if (cleanedLabel) {
    return cleanedLabel;
  }

  if (DEFAULT_CATEGORY_LABELS[categoryKey]) {
    return DEFAULT_CATEGORY_LABELS[categoryKey];
  }

  return categoryKey
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const parseDateValue = (value) => {
  if (!value) {
    return 0;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const extractDateFromPath = (pathParts) => {
  const year = pathParts[0];
  const month = pathParts[1];
  const day = pathParts[2];

  if (!/^\d{4}$/.test(year) || !/^\d{2}$/.test(month) || !/^\d{2}$/.test(day)) {
    return '';
  }

  return `${year}-${month}-${day}T00:00:00.000Z`;
};

const extractMarkdownBody = (markdown) => {
  const normalized = markdown.replace(/^\uFEFF/, '');

  if (!normalized.startsWith('---')) {
    return normalized.trim();
  }

  const lines = normalized.split(/\r?\n/);
  if (lines[0].trim() !== '---') {
    return normalized.trim();
  }

  for (let i = 1; i < lines.length; i += 1) {
    if (lines[i].trim() === '---') {
      return lines
        .slice(i + 1)
        .join('\n')
        .trim();
    }
  }

  return normalized.trim();
};

const loadMarkdownFile = async (key) => {
  const moduleValue = movieSubmissionsContext(key);
  const url = moduleValue.default || moduleValue;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to load movie submission ${key}`);
  }

  if (typeof TextDecoder !== 'undefined') {
    const buffer = await response.arrayBuffer();
    return new TextDecoder('utf-8').decode(buffer);
  }

  return response.text();
};

const createMovieSubmission = (markdown, key) => {
  const parsed = parseMarkdown(markdown);
  const frontmatter = parsed.frontmatter || {};
  const relativePath = key.replace(/^\.\//, '');
  const pathParts = relativePath.split(/[\\/]/);
  const directoryParts = pathParts.slice(0, -1);
  const subDirs = directoryParts.slice(3);
  const fileName = pathParts[pathParts.length - 1] || '';
  const pathColumn = subDirs.length >= 2 ? subDirs[0] : '';
  const pathCategory = subDirs.length >= 2 ? subDirs[1] : subDirs[0] || '';
  const column = normalizeColumn(frontmatter.column || pathColumn || 'favoritos');
  const { categoryKey, category } = resolveCategory(
    frontmatter.categoryKey,
    frontmatter.category,
    pathCategory
  );

  const title =
    normalizeWhitespace(frontmatter.title) ||
    fileName.replace(/\.md$/, '').replace(/[-_]+/g, ' ').trim() ||
    'Sem titulo';

  const pathDate = extractDateFromPath(pathParts);
  const createdAt = normalizeWhitespace(frontmatter.createdAt) || pathDate;
  const body = extractMarkdownBody(markdown);

  return {
    id: relativePath,
    path: relativePath,
    title,
    column,
    categoryKey,
    category,
    createdAt,
    timestamp: parseDateValue(createdAt) || parseDateValue(pathDate),
    note: body,
    noteHtml: body ? parsed.html : '',
  };
};

const setMovieSubmissionsContextForTests = (context) => {
  movieSubmissionsContext = context || createMovieSubmissionsContext();
  clearMovieSubmissionsCache();
};

const resetMovieSubmissionsContextForTests = () => {
  movieSubmissionsContext = createMovieSubmissionsContext();
  clearMovieSubmissionsCache();
};

export const __movieSubmissionsTestUtils = {
  normalizeWhitespace,
  normalizeColumn,
  normalizeCategoryKey,
  resolveCategory,
  formatCategoryLabel,
  parseDateValue,
  extractDateFromPath,
  extractMarkdownBody,
  createMovieSubmission,
  setMovieSubmissionsContextForTests,
  resetMovieSubmissionsContextForTests,
};

let movieSubmissionsCache = null;

export const loadAllMovieSubmissions = async () => {
  if (movieSubmissionsCache) {
    return movieSubmissionsCache;
  }

  movieSubmissionsCache = Promise.all(
    movieSubmissionsContext.keys().map(async (key) => {
      try {
        const markdown = await loadMarkdownFile(key);
        return createMovieSubmission(markdown, key);
      } catch {
        return null;
      }
    })
  ).then((items) => {
    return items.filter(Boolean).sort((a, b) => {
      if (b.timestamp !== a.timestamp) {
        return b.timestamp - a.timestamp;
      }

      return a.title.localeCompare(b.title, 'pt-BR');
    });
  });

  return movieSubmissionsCache;
};

export const getAllMovieSubmissions = () => loadAllMovieSubmissions();

export const clearMovieSubmissionsCache = () => {
  movieSubmissionsCache = null;
};
